import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  TablePagination,
  Button,
  TableSortLabel,
  TextField,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SortIcon from '@mui/icons-material/Sort';
import SearchIcon from '@mui/icons-material/Search';
import { visuallyHidden } from '@mui/utils';
import { accountingApi } from '../../../config/apiClient';
import { useAuth } from '../../../contexts/AuthContext';

// Stok ürün arayüzü
interface InventoryItem {
  id: string;
  name: string;
  stockCode: string;
  salePrice: number;
  saleCurrency?: string;
  imageUrl?: string | null;
  isActive: boolean;
  categoryId?: string;
  categoryName?: string; // Backend'den gelecek
  brandId?: string;
  brandName?: string; // Backend'den gelecek
  quantity: number;
  unit?: string;
}

interface Category {
  id: string;
  name: string;
}

interface Brand {
  id: string;
  name: string;
}

type SortableKeys = 'quantity' | 'salePrice';
type Order = 'asc' | 'desc';

interface HeadCell {
  id: SortableKeys | 'image' | 'name' | 'category' | 'brand' | 'stockCode';
  label: string;
  numeric: boolean;
  sortable: boolean;
}

const headCells: readonly HeadCell[] = [
    { id: 'image', numeric: false, label: 'Resim', sortable: false },
    { id: 'name', numeric: false, label: 'Ürün Adı', sortable: false },
    { id: 'category', numeric: false, label: 'Kategori', sortable: false },
    { id: 'brand', numeric: false, label: 'Marka', sortable: false },
    { id: 'stockCode', numeric: false, label: 'Stok Kodu', sortable: false },
    { id: 'quantity', numeric: true, label: 'Miktar', sortable: true },
    { id: 'salePrice', numeric: true, label: 'Satış Fiyatı', sortable: true },
];

interface GeneralProductsTabProps {
  companyId: string | null;
}

const GeneralProductsTab: React.FC<GeneralProductsTabProps> = ({ companyId }) => {
  // ✅ Merkezi Cache Sistemi - AuthContext'ten al
  const { categories, brands } = useAuth();
  
  const [products, setProducts] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<SortableKeys | null>(null);

  // Filtreler için state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [stockFilter, setStockFilter] = useState<{ id: string; label: string } | null>(null);

  // ✅ SQL'den e-ticarete açık ürünleri yükle
  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    loadData();
  }, [companyId]);

  const loadData = async () => {
    if (!companyId) return;

    setLoading(true);
    setError(null);

    try {
      console.log('🛒 [GENERAL PRODUCTS] SQL\'den ürünler yükleniyor...');

      // ✅ Kategoriler ve markalar artık AuthContext'ten geliyor
      // Sadece ürünleri çek
      const productsResponse = await accountingApi.get(`/api/Inventory/items/ecommerce`);

      console.log('✅ [GENERAL PRODUCTS] Ürünler:', productsResponse.data.length);

      // Backend'den gelen DTO'ları map et
      const productList: InventoryItem[] = productsResponse.data.map((item: any) => ({
        id: item.id,
        name: item.name || '',
        stockCode: item.stockCode || '',
        salePrice: item.salePrice || 0,
        saleCurrency: item.saleCurrency || 'TRY',
        imageUrl: item.imageUrl || null,
        isActive: item.isActive ?? true,
        categoryId: item.categoryId || null,
        categoryName: item.categoryName || null,
        brandId: item.brandId || null,
        brandName: item.brandName || null,
        quantity: item.quantity || 0,
        unit: item.unit || 'adet',
      }));

      setProducts(productList);
    } catch (err: any) {
      console.error('❌ [GENERAL PRODUCTS] Yükleme hatası:', err);
      setError('Veriler yüklenirken bir hata oluştu: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(0);
  }, [searchTerm, selectedBrand, selectedCategory, stockFilter]);

  const handleSortRequest = (property: SortableKeys) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleResetSort = () => {
    setOrderBy(null);
  };

  const brandOptions = useMemo(
    () => brands.sort((a, b) => a.name.localeCompare(b.name)),
    [brands]
  );

  const categoryOptions = useMemo(
    () => categories.sort((a, b) => a.name.localeCompare(b.name)),
    [categories]
  );
  
  const stockFilterOptions = [
    { id: 'inStock', label: 'Stokta Olanlar' },
    { id: 'outOfStock', label: 'Stokta Olmayanlar' },
  ];

  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const searchMatch = searchTerm.trim() === '' ||
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.stockCode.toLowerCase().includes(searchTerm.toLowerCase());

      const brandMatch = !selectedBrand || product.brandId === selectedBrand.id;
      const categoryMatch = !selectedCategory || product.categoryId === selectedCategory.id;
      const stockMatch = !stockFilter ||
        (stockFilter.id === 'inStock' && product.quantity > 0) ||
        (stockFilter.id === 'outOfStock' && (product.quantity <= 0 || product.quantity === undefined));

      return searchMatch && brandMatch && categoryMatch && stockMatch;
    });
  }, [products, searchTerm, selectedBrand, selectedCategory, stockFilter]);

  const sortedProducts = useMemo(() => {
    if (!orderBy) {
      return filteredProducts;
    }

    return [...filteredProducts].sort((a, b) => {
      const aValue = a[orderBy] ?? 0;
      const bValue = b[orderBy] ?? 0;

      if (bValue < aValue) {
        return order === 'desc' ? -1 : 1;
      }
      if (bValue > aValue) {
        return order === 'desc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredProducts, order, orderBy]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getCurrencySymbol = (currency: string): string => {
        switch(currency) {
        case 'TRY': return '₺';
        case 'USD': return '$';
        case 'EUR': return '€';
        default: return currency;
        }
    };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
  }

  return (
    <Box>
        {/* ✅ SQL Entegrasyonu Bildirimi */}
        <Alert severity="info" sx={{ mb: 2 }}>
          ✅ <strong>SQL Entegrasyonu Aktif</strong> - E-ticarete açık ürünler SQL veritabanından çekiliyor (Read-Only)
        </Alert>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
                Bu koleksiyonda bulunan {products.length} üründen {sortedProducts.length} tanesi gösteriliyor.
            </Typography>
            {orderBy && (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleResetSort}
                    startIcon={<SortIcon />}
                >
                    Sıralamayı Sıfırla
                </Button>
            )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
            <TextField
                size="small"
                placeholder="Ürün Ara (Ad, Stok Kodu)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    ),
                }}
                sx={{ width: 250 }}
            />
            <Autocomplete
                size="small"
                options={brandOptions}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={selectedBrand}
                onChange={(_, newValue) => setSelectedBrand(newValue)}
                renderInput={(params) => <TextField {...params} placeholder="Marka" sx={{ width: 200 }} />}
            />
            <Autocomplete
                size="small"
                options={categoryOptions}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={selectedCategory}
                onChange={(_, newValue) => setSelectedCategory(newValue)}
                renderInput={(params) => <TextField {...params} placeholder="Kategori" sx={{ width: 200 }} />}
            />
            <Autocomplete
                size="small"
                options={stockFilterOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={stockFilter}
                onChange={(_, newValue) => setStockFilter(newValue)}
                renderInput={(params) => <TextField {...params} placeholder="Stok Durumu" sx={{ width: 200 }} />}
            />
        </Box>
      <Paper variant="outlined">
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        sx={{ fontWeight: 'bold' }}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        {headCell.sortable ? (
                            <TableSortLabel
                                active={orderBy === headCell.id}
                                direction={orderBy === headCell.id ? order : 'asc'}
                                onClick={() => handleSortRequest(headCell.id as SortableKeys)}
                            >
                                {headCell.label}
                                {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                                ) : null}
                            </TableSortLabel>
                        ) : (
                            headCell.label
                        )}
                    </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedProducts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={headCells.length} align="center">
                    <Typography color="text.secondary" sx={{ p: 4 }}>
                      E-ticarete açık ürün bulunmamaktadır.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedProducts
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((product) => (
                    <TableRow key={product.id} hover>
                      <TableCell>
                        {product.imageUrl ? (
                          <Box
                            component="img"
                            src={product.imageUrl.split(',')[0]}
                            alt={product.name}
                            sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1 }}
                          />
                        ) : (
                          <Box sx={{ width: 40, height: 40, bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1 }}>
                            <PhotoCameraIcon fontSize="small" color="action" />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontWeight: '500' }}>{product.name}</TableCell>
                      <TableCell>{product.categoryName || '—'}</TableCell>
                      <TableCell>{product.brandName || '—'}</TableCell>
                      <TableCell>{product.stockCode}</TableCell>
                      <TableCell align="right">{product.quantity ?? 0}</TableCell>
                      <TableCell align="right">
                        {`${product.salePrice.toLocaleString('tr-TR')} ${getCurrencySymbol(product.saleCurrency || 'TRY')}`}
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
            rowsPerPageOptions={[100, 200]}
            component="div"
            count={sortedProducts.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Sayfa başına ürün:"
            labelDisplayedRows={({ from, to, count }) => `${count} üründen ${from}–${to} arası`}
        />
      </Paper>
    </Box>
  );
};

export default GeneralProductsTab;
